<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
  settings: {
    type: Object,
    default: () => ({}),
  },
  iframeClass: {
    type: String,
    default: '',
  },
  emptyClass: {
    type: String,
    default: '',
  },
});

const sdkFrame = ref(null);
const previewDocHtml = ref('');

const livePreviewBaseUrl = computed(() => {
  const script = props.inbox.web_widget_script || '';
  const match = script.match(/var\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/);
  return match?.[1] || window.location.origin;
});

const livePreviewWebsiteToken = computed(() => props.inbox.website_token || '');

const buildPreviewDoc = () => {
  if (!livePreviewWebsiteToken.value) return '';
  const isMidnight = props.inbox.widget_theme_preset === 'midnight';
  const bg =
    isMidnight && props.inbox.widget_theme_config?.background
      ? props.inbox.widget_theme_config.background
      : isMidnight
        ? '#111111'
        : '#ffffff';
  const settingsJSON = JSON.stringify(props.settings || {})
    .split('<')
    .join('\\u003c');
  const baseUrl = JSON.stringify(livePreviewBaseUrl.value);
  const token = JSON.stringify(livePreviewWebsiteToken.value);
  const SO = '\x3cscript\x3e';
  const SC = '\x3c\x2fscript\x3e';

  return (
    `<!doctype html><html><head>` +
    `<meta charset="utf-8"/>` +
    `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
    `<style>html,body{margin:0;width:100%;height:100%;background:${bg};overflow:hidden;}` +
    `body{position:fixed;inset:0;overflow:hidden;}` +
    `.woot--bubble-holder{visibility:hidden;transition:visibility 0s;}</style>` +
    `</head><body>` +
    SO +
    `window.wlabSettings=${settingsJSON};` +
    `window.__wlabForward=function(s){` +
    `var f=document.getElementById('chatwoot_live_chat_widget');` +
    `if(!f||!f.contentWindow)return false;` +
    `var nextLocale=(s&&(s.widget_language||s.locale))||'en';` +
    `f.contentWindow.postMessage('chatwoot-widget:'+JSON.stringify(` +
    `Object.assign({event:'config-set'},s,{locale:nextLocale,widget_language:nextLocale,showUnreadMessagesDialog:true,isPreviewUpdate:true})),'*');` +
    `if(s&&s.darkMode)f.contentWindow.postMessage('chatwoot-widget:'+` +
    `JSON.stringify({event:'set-color-scheme',darkMode:s.darkMode}),'*');` +
    `return true;};` +
    `window.addEventListener('message',function(e){` +
    `try{var d;if(typeof e.data==='string')d=JSON.parse(e.data);else d=e.data;` +
    `if(d&&d.type==='wlab-settings'){` +
    `window.wlabSettings=d.s||{};window.__wlabForward(window.wlabSettings);` +
    `}}catch(x){}});` +
    `(function(d,t){var B=${baseUrl},T=${token};` +
    `var g=d.createElement(t),s=d.getElementsByTagName(t)[0];` +
    `g.src=B+"/packs/js/sdk.js?v="+Date.now();g.async=true;` +
    `s.parentNode.insertBefore(g,s);` +
    `g.onload=function(){window.wlabSDK.run({websiteToken:T,baseUrl:B});` +
    `setTimeout(function(){if(window.$chatwoot)window.$chatwoot.toggle('open');},800);` +
    `var __tries=0;var __syncTimer=setInterval(function(){` +
    `__tries+=1;` +
    `if(window.__wlabForward(window.wlabSettings)||__tries>20){clearInterval(__syncTimer);}` +
    `},300);` +
    `setTimeout(function(){setInterval(function(){` +
    `var wh=document.querySelector('.woot-widget-holder');` +
    `var bh=document.querySelector('.woot--bubble-holder');` +
    `if(!wh||!bh)return;` +
    `bh.style.visibility=wh.classList.contains('woot--hide')?'visible':'hidden';` +
    `},250);},1600);};` +
    `})(document,"script");` +
    SC +
    `</body></html>`
  );
};

watch(
  () => props.inbox,
  () => {
    previewDocHtml.value = buildPreviewDoc();
  },
  { immediate: true, deep: true }
);

const pushSettingsToPreview = () => {
  const iframe = sdkFrame.value;
  if (!iframe?.contentWindow) return;
  try {
    iframe.contentWindow.postMessage(
      JSON.stringify({ type: 'wlab-settings', s: props.settings || {} }),
      '*'
    );
  } catch (_) {
    // ignore preview sync failures
  }
};

watch(
  () => props.settings,
  () => {
    pushSettingsToPreview();
  },
  { deep: true }
);
</script>

<template>
  <iframe
    v-if="previewDocHtml"
    ref="sdkFrame"
    :class="iframeClass"
    :srcdoc="previewDocHtml"
    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
  />
  <div v-else :class="emptyClass">
    Widget preview unavailable for this inbox.
  </div>
</template>
